/**
 * Global Tracking Configuration
 * Centralized event naming and consent management for castrol-flow tracking
 */

// Event naming convention: castrol_flow_{flow}_{action}
// where flow is the user journey and action is the specific event
export const TRACKING_EVENTS = {
  // === SESSION & AUTH FLOWS ===
  SESSION_MODAL_SHOWN: 'castrol_flow_session_timeout_modal_shown',
  SESSION_REFRESH_CLICK: 'castrol_flow_session_refresh_click',
  SESSION_REFRESH_SUCCESS: 'castrol_flow_session_refresh_success',
  SESSION_REFRESH_FAILURE: 'castrol_flow_session_refresh_failure',
  SESSION_SIGN_IN_CLICK: 'castrol_flow_session_sign_in_click',
  SESSION_BANNER_SHOWN: 'castrol_flow_session_banner_shown',
  SESSION_REDIRECT_TO_AUTH: 'castrol_flow_session_redirect_to_auth',

  // === ERROR HANDLING FLOWS ===
  ERROR_PAGE_VIEWED: 'castrol_flow_error_page_viewed',
  ERROR_PAGE_RETRY: 'castrol_flow_error_page_retry',
  ERROR_PAGE_REDIRECT: 'castrol_flow_error_page_redirect',
  APOLLO_ERROR_RETRY: 'castrol_flow_apollo_error_retry',
  APOLLO_ERROR_EXHAUSTED: 'castrol_flow_apollo_error_exhausted',

  // === CREATE ORDER FLOW ===
  CREATE_ORDER_FORM_START: 'castrol_flow_create_order_form_start',
  CREATE_ORDER_PORT_SELECT: 'castrol_flow_create_order_port_select',
  CREATE_ORDER_VESSEL_SELECT: 'castrol_flow_create_order_vessel_select',
  CREATE_ORDER_FUEL_SELECT: 'castrol_flow_create_order_fuel_select',
  CREATE_ORDER_FORM_SUBMIT: 'castrol_flow_create_order_form_submit',
  CREATE_ORDER_SUCCESS: 'castrol_flow_create_order_success',
  CREATE_ORDER_FAILURE: 'castrol_flow_create_order_failure',
  CREATE_ORDER_PORT_CHANGE_MODAL: 'castrol_flow_create_order_port_change_modal',
  CREATE_ORDER_RUSH_ORDER_MODAL: 'castrol_flow_create_order_rush_order_modal',

  // === CART FLOWS ===
  CART_VIEW: 'castrol_flow_cart_view',
  CART_ADD_ITEM: 'castrol_flow_cart_add_item',
  CART_REMOVE_ITEM: 'castrol_flow_cart_remove_item',
  CART_UPDATE_QUANTITY: 'castrol_flow_cart_update_quantity',
  CART_CHECKOUT_START: 'castrol_flow_cart_checkout_start',
  CART_DELIVERY_OPTION_SELECT: 'castrol_flow_cart_delivery_option_select',

  // === NAVIGATION & REDIRECTS ===
  PAGE_NAVIGATION: 'castrol_flow_page_navigation',
  LINK_CLICK: 'castrol_flow_link_click',
  BUTTON_CLICK: 'castrol_flow_button_click',
  REDIRECT_TRIGGERED: 'castrol_flow_redirect_triggered',

  // === USER INTERACTIONS ===
  SEARCH_PERFORMED: 'castrol_flow_search_performed',
  FILTER_APPLIED: 'castrol_flow_filter_applied',
  DROPDOWN_OPENED: 'castrol_flow_dropdown_opened',
  MODAL_OPENED: 'castrol_flow_modal_opened',
  MODAL_CLOSED: 'castrol_flow_modal_closed',

  // === AUTHENTICATION ===
  LOGIN_STARTED: 'castrol_flow_login_started',
  LOGIN_SUCCESS: 'castrol_flow_login_success',
  LOGIN_FAILURE: 'castrol_flow_login_failure',
  LOGOUT: 'castrol_flow_logout',
  TOKEN_REFRESH: 'castrol_flow_token_refresh',
  TOKEN_VALIDATION: 'castrol_flow_token_validation'
};

// Check if consent is granted for analytics
export const isAnalyticsConsented = () => {
  if (typeof window === 'undefined') return false;

  try {
    // Check gtag consent settings
    if (window.gtag) {
      // We can check against localStorage or session context
      // eslint-disable-next-line no-underscore-dangle
      const consentSettings = window.__analyticsConsent || {};
      return consentSettings.analytics_storage !== 'denied';
    }

    // Fallback: check for cookie consent
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      try {
        const consent = JSON.parse(cookieConsent);
        return consent.analytics_storage === 'granted' || consent.analytics === true;
      } catch (e) {
        // If not JSON, assume consent denied
        return false;
      }
    }

    // Default: deny tracking if no explicit consent found
    return false;
  } catch (error) {
    console.warn('⚠️  Error checking analytics consent:', error?.message);
    return false;
  }
};

// Track event with consent check
export const trackEventWithConsent = (trackEvent, eventName, eventData = {}) => {
  if (!isAnalyticsConsented()) {
    console.debug(`[GTM] Skipping event (no consent): ${eventName}`);
    return;
  }

  trackEvent(eventName, eventData);
};

// Event context helpers for consistent data structure
export const getEventContext = () => ({
  page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  page_url: typeof window !== 'undefined' ? window.location.href : '',
  timestamp: new Date().toISOString()
});

// Button click context
export const getButtonContext = (buttonElement, buttonName) => ({
  button_name: buttonName || buttonElement?.id || buttonElement?.className || 'unknown',
  button_text: buttonElement?.textContent?.trim() || '',
  button_class: buttonElement?.className || '',
  ...getEventContext()
});

// Link click context
export const getLinkContext = (linkElement, linkName) => ({
  link_name: linkName || linkElement?.id || linkElement?.href || 'unknown',
  link_href: linkElement?.href || '',
  link_text: linkElement?.textContent?.trim() || '',
  ...getEventContext()
});

// Navigation context
export const getNavigationContext = (fromPath, toPath, navigationType = 'link') => ({
  from_page: fromPath,
  to_page: toPath,
  navigation_type: navigationType,
  ...getEventContext()
});

// Error context
export const getErrorContext = (errorType, errorMessage = '', details = {}) => ({
  error_type: errorType,
  error_message: errorMessage.substring(0, 200), // Limit message length
  ...details,
  ...getEventContext()
});

// Form submission context
export const getFormContext = (formName, formStatus = 'submitted', formData = {}) => ({
  form_name: formName,
  form_status: formStatus,
  form_fields: Object.keys(formData || {}), // Only field names, not values
  ...getEventContext()
});

export default {
  TRACKING_EVENTS,
  isAnalyticsConsented,
  trackEventWithConsent,
  getEventContext,
  getButtonContext,
  getLinkContext,
  getNavigationContext,
  getErrorContext,
  getFormContext
};
